import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
interface SyrupSelectionProps {
  bibSyrups: string[];
  bottleSyrups: Record<string, number>;
  handleCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBottleQuantityChange: (name: string, quantity: number) => void;
  onSkip?: () => void;
}
export function SyrupSelection({
  bibSyrups,
  bottleSyrups,
  handleCheckboxChange,
  handleBottleQuantityChange,
  onSkip
}: SyrupSelectionProps) {
  const bibOptions = ['Vlierbloesem', 'Citroen', 'Iced Tea', 'Blood Orange', 'Ginger Lime'];
  const bottleOptions = ['Blood Orange', 'Citroen', 'Cranberry Rose Hip', 'Dennen Agave', 'Ginger Lime', 'Ice Tea', 'Maté Ginseng', 'Rabarber', 'Sassafras Rootbeer', 'Tonic', 'Vlierbloesem', 'Strawberry', 'Zwarte Bes'];
  // Function to handle direct input changes with a max of 600
  const handleDirectInputChange = (syrup: string, value: string) => {
    const numValue = parseInt(value) || 0;
    const clampedValue = Math.min(600, Math.max(0, numValue));
    handleBottleQuantityChange(syrup, clampedValue);
  };
  // Function to round slider values to multiples of 6
  const roundToSix = (value: number) => {
    return Math.round(value / 6) * 6;
  };
  return <div className="space-y-6" data-id="element-120">
      <div className="flex justify-between items-center" data-id="element-121">
        <h2 className="text-xl font-semibold text-[#052858]" data-id="element-122">
          Siropen Selectie{' '}
          <span className="text-sm font-normal text-gray-500" data-id="element-123">(optioneel)</span>
        </h2>
      </div>

      {/* Skip option at the top */}
      {onSkip && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-124">
          <div className="flex items-center justify-between" data-id="element-125">
            <div data-id="element-126">
              <p className="text-sm font-medium text-gray-700" data-id="element-127">
                Geen siropen nodig?
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-128">
                Sla deze stap over en ga direct naar de foto's
              </p>
            </div>
            <button type="button" onClick={onSkip} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#19EAC4] to-[#052858] text-white rounded-md hover:opacity-90 transition-opacity text-sm font-medium" data-id="element-129">
              Volgende
              <ArrowRight size={16} data-id="element-130" />
            </button>
          </div>
        </div>}

      <div className="bg-[#C8FAF1] bg-opacity-30 p-5 rounded-lg shadow-sm" data-id="element-131">
        <div className="flex flex-col md:flex-row gap-5 mb-4" data-id="element-132">
          <div className="flex-1" data-id="element-133">
            <h3 className="font-medium text-[#052858] mb-1" data-id="element-134">
              BiB (Bag-in-Box) Siropen
            </h3>
            <p className="text-sm font-normal text-gray-500 mb-3" data-id="element-135">
              (indien van toepassing)
            </p>
            <div className="mb-4 p-3 bg-white bg-opacity-50 rounded-md" data-id="element-136">
              <p className="text-sm text-gray-700" data-id="element-137">
                Indien u heeft gekozen voor een siropenbar, kunt u hieronder
                twee of drie smaken selecteren.
              </p>
            </div>
          </div>
          <div className="md:w-2/5 flex items-center justify-center" data-id="element-138">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kjcBHsgca86QpyGFztNfak/KRNWTR2B_-_productfoto_-_siropenbar_3_%281%29.jpg" alt="KRNWTR+ Siropenbar" className="rounded-lg shadow-md max-h-48 object-contain" data-id="element-139" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-140">
          {bibOptions.map(syrup => <div key={`bib-${syrup}`} className="flex items-center bg-white bg-opacity-50 p-2 rounded-md" data-id="element-141">
              <input type="checkbox" id={`bib-${syrup}`} name={`bib-${syrup}`} checked={bibSyrups.includes(syrup)} onChange={handleCheckboxChange} className="h-5 w-5 text-[#19EAC4] focus:ring-[#19EAC4] border-gray-300 rounded" data-id="element-142" />
              <label htmlFor={`bib-${syrup}`} className="ml-2 block text-sm text-gray-700 font-medium" data-id="element-143">
                {syrup}
              </label>
            </div>)}
        </div>
        <div className="mt-3 p-3 bg-white bg-opacity-50 rounded-md" data-id="element-144">
          <p className="text-sm text-gray-700" data-id="element-145">
            * Heeft u in de offerte gekozen voor meer dan drie smaken? Vul de
            gewenste extra smaken dan gerust in onder "Overige opmerkingen" op
            de vorige pagina.
          </p>
        </div>
      </div>
      <div className="bg-[#FFD6E3] bg-opacity-30 p-5 rounded-lg shadow-sm" data-id="element-146">
        <div className="flex flex-col md:flex-row gap-5 mb-4" data-id="element-147">
          <div className="flex-1" data-id="element-148">
            <h3 className="font-medium text-[#052858] mb-1" data-id="element-149">
              Flessen Siropen (1L)
            </h3>
            <p className="text-sm font-normal text-gray-500 mb-3" data-id="element-150">
              (indien van toepassing)
            </p>
            <div className="mb-4 p-3 bg-white bg-opacity-50 rounded-md" data-id="element-151">
              <p className="text-sm text-gray-700" data-id="element-152">
                De siropenflessen worden geleverd per doos van 6. U kunt hierbij
                verschillende smaken combineren.
              </p>
            </div>
          </div>
          <div className="md:w-1/3 flex items-center justify-center" data-id="element-153">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/m1iR6gWtGrJZuPyxM49r1P/unnamed.png" alt="KRNWTR+ Siropen Flessen" className="rounded-lg shadow-md max-h-48 object-contain" data-id="element-154" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-155">
          {bottleOptions.map(syrup => <div key={`bottle-${syrup}`} className="bg-white bg-opacity-50 p-3 rounded-md" data-id="element-156">
              <div className="flex justify-between items-center mb-1" data-id="element-157">
                <span className="text-sm font-medium text-gray-700" data-id="element-158">
                  {syrup}
                </span>
                <div className="flex items-center" data-id="element-159">
                  <input type="number" min="0" max="600" value={bottleSyrups[syrup] || 0} onChange={e => handleDirectInputChange(syrup, e.target.value)} className="w-16 h-8 text-center border border-[#19EAC4] rounded-md focus:outline-none focus:ring-2 focus:ring-[#19EAC4]" data-id="element-160" />
                </div>
              </div>
              <div className="flex items-center gap-2" data-id="element-161">
                <span className="text-xs text-gray-500" data-id="element-162">0</span>
                <input type="range" min="0" max="60" step="6" value={Math.min(60, bottleSyrups[syrup] || 0)} onChange={e => handleBottleQuantityChange(syrup, parseInt(e.target.value))} className="flex-grow h-2 appearance-none bg-gray-200 rounded-full accent-[#FF558A] focus:outline-none" data-id="element-163" />
                <span className="text-xs text-gray-500" data-id="element-164">60</span>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}